import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const FavoritesContext = createContext({ ids: new Set(), toggle: () => {}, isFavorited: () => false });

export const useFavorites = () => useContext(FavoritesContext);

export function FavoritesProvider({ children }) {
  const [ids, setIds] = useState(new Set());
  const authToken  = localStorage.getItem("authToken");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!authToken) return;
    axios
      .get(`${backendUrl}/api/favorites/ids`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => setIds(new Set(r.data)))
      .catch(() => {});
  }, [authToken, backendUrl]);

  const toggle = useCallback(async (productId) => {
    if (!authToken) return false;
    // Optimistic update
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
    try {
      const res = await axios.post(
        `${backendUrl}/api/toggle-favorite/${productId}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      // Sync with server truth
      setIds((prev) => {
        const next = new Set(prev);
        if (res.data.isFavorited) next.add(productId);
        else next.delete(productId);
        return next;
      });
      return res.data.isFavorited;
    } catch {
      // Rollback
      setIds((prev) => {
        const next = new Set(prev);
        if (next.has(productId)) next.delete(productId);
        else next.add(productId);
        return next;
      });
      return null;
    }
  }, [authToken, backendUrl]);

  const isFavorited = useCallback((productId) => ids.has(productId), [ids]);

  return (
    <FavoritesContext.Provider value={{ ids, toggle, isFavorited }}>
      {children}
    </FavoritesContext.Provider>
  );
}
